// src/utils/buildChallengesContextForAI.ts
import { supabase } from '@/integrations/supabase/client';

interface WellnessChallengeRow {
  id: string;
  template_id: string | null;
  custom_title: string | null;
  custom_goal: string | null;
  category: string;
  metric_source: string;
  metric_key: string | null;
  target_type: string;
  target_value: number | null;
  unit: string | null;
  duration_days: number;
  start_date: string;
  end_date: string;
  status: string;
  reason_text: string | null;
  created_at: string;
  completed_at: string | null;
  template_title?: string | null;
  template_short_label?: string | null;
}

interface CheckinAgg {
  user_challenge_id: string;
  total_days: number;
  completed_days: number;
}

interface GroupChallengeRow {
  id: string;
  title: string | null;
  description?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

export async function buildChallengesContextForAI(userId: string): Promise<string> {
  const [wellnessText, groupText] = await Promise.all([
    buildWellnessChallengesSummary(userId),
    buildGroupChallengesSummary(userId)
  ]);

  if (!wellnessText && !groupText) {
    return '';
  }

  let text = `Here is a snapshot of the user's current challenge landscape:\n\n`;

  if (wellnessText) {
    text += wellnessText + '\n\n';
  }
  if (groupText) {
    text += groupText + '\n\n';
  }

  text +=
    'These details are for gentle context only - please respond in a supportive, non-judgemental way, focusing on encouragement and small next steps rather than perfection.';

  return text;
}

async function buildWellnessChallengesSummary(userId: string): Promise<string> {
  // 1) Fetch the user's wellness challenges
  const { data: challenges, error } = await supabase
    .from('wellness_user_challenges')
    .select(
      `
      *,
      wellness_challenge_templates (
        title,
        short_label
      )
    `
    )
    .eq('user_id', userId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching wellness_user_challenges', error);
    return '';
  }

  const challengeRows = (challenges || []) as (WellnessChallengeRow & {
    wellness_challenge_templates?: { title: string | null; short_label: string | null } | null;
  })[];

  if (!challengeRows.length) {
    return '';
  }

  const active = challengeRows.filter(ch => ch.status === 'active');
  const completed = challengeRows.filter(ch => ch.status === 'completed');

  // 2) Fetch check-in aggregates for all these challenges
  const ids = challengeRows.map(ch => ch.id);
  const { data: checkins, error: checkError } = await supabase
    .from('wellness_challenge_checkins')
    .select('user_challenge_id, completed')
    .in('user_challenge_id', ids);

  if (checkError) {
    console.error('Error fetching wellness_challenge_checkins', checkError);
  }

  const aggByChallenge = new Map<string, CheckinAgg>();

  for (const c of checkins || []) {
    const id = c.user_challenge_id as string;
    const existing = aggByChallenge.get(id) || {
      user_challenge_id: id,
      total_days: 0,
      completed_days: 0
    };

    existing.total_days += 1;
    if (c.completed) {
      existing.completed_days += 1;
    }

    aggByChallenge.set(id, existing);
  }

  const describeChallenge = (ch: WellnessChallengeRow): string => {
    const label =
      (ch as any).wellness_challenge_templates?.short_label ||
      (ch as any).wellness_challenge_templates?.title ||
      ch.custom_title ||
      'Wellness challenge';

    const agg = aggByChallenge.get(ch.id);
    let progress = '';

    if (agg && agg.total_days > 0) {
      const completionRate = (agg.completed_days / agg.total_days) * 100;
      progress = ` (about ${Math.round(completionRate)}% of days completed so far)`;
    }

    const duration = ch.duration_days ? `for ${ch.duration_days} days` : '';
    const why = ch.reason_text ? ` - intention: ${ch.reason_text}` : '';

    return `• ${label}${duration ? ` (${duration})` : ''}${progress}${why}`;
  };

  let text = 'Personal wellness challenges:\n';

  if (active.length) {
    text += '- Active focuses:\n';
    for (const ch of active) {
      text += describeChallenge(ch) + '\n';
    }
  }

  if (completed.length) {
    text += '- Recently completed:\n';
    // show at most the last 3 completed
    const sortedCompleted = [...completed].sort((a, b) =>
      (b.completed_at || '').localeCompare(a.completed_at || '')
    );
    for (const ch of sortedCompleted.slice(0, 3)) {
      text += describeChallenge(ch) + '\n';
    }
  }

  return text.trim();
}

async function buildGroupChallengesSummary(userId: string): Promise<string> {
  // If group challenge tables don't exist or are unused, this will just return an empty string.
  // We keep the query conservative and let Supabase / Lovable adjust field names if needed.
  const { data: participants, error } = await supabase
    .from('challenge_participants')
    .select('challenge_id, completion_status')
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching challenge_participants', error);
    return '';
  }

  if (!participants || !participants.length) {
    return '';
  }

  const challengeIds = [...new Set(participants.map(p => p.challenge_id as string))];

  const { data: challenges, error: gcError } = await supabase
    .from('group_challenges')
    .select('id, title, description, start_date, end_date')
    .in('id', challengeIds);

  if (gcError) {
    console.error('Error fetching group_challenges', gcError);
    return '';
  }

  const byId = new Map<string, GroupChallengeRow>();
  for (const c of challenges || []) {
    byId.set(c.id as string, {
      id: c.id as string,
      title: c.title ?? null,
      description: c.description ?? null,
      start_date: c.start_date ?? null,
      end_date: c.end_date ?? null
    });
  }

  const active: string[] = [];
  const completed: string[] = [];

  for (const p of participants) {
    const challenge = byId.get(p.challenge_id as string);
    if (!challenge) continue;

    const title = challenge.title || 'Group challenge';
    const status = p.completion_status || 'active';

    const bullet = `• ${title} (${status})`;

    if (status === 'completed') {
      completed.push(bullet);
    } else {
      active.push(bullet);
    }
  }

  if (!active.length && !completed.length) {
    return '';
  }

  let text = 'Group / community challenges:\n';

  if (active.length) {
    text += '- Currently participating in:\n';
    active.forEach(line => (text += line + '\n'));
  }

  if (completed.length) {
    text += '- Completed previously:\n';
    completed.slice(0, 3).forEach(line => (text += line + '\n'));
  }

  return text.trim();
}
